import React from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { FiChevronDown } from 'react-icons/fi';

function optionLabel(driver) {
  return `${driver.driver_name || driver.driver_id}${driver.has_garage ? '' : ' (no garage)'}`;
}

export default function DriverPicker({ value, drivers, onChange, error, ariaLabel }) {
  const [query, setQuery] = React.useState('');
  const known = drivers.find((d) => d.driver_id === value);

  const options = React.useMemo(() => {
    const list = [{ id: '', label: 'Select driver…' }];
    if (value && !known) {
      list.push({ id: value, label: `Inactive driver (${value.slice(-6)})` });
    }
    drivers.forEach((d) => {
      list.push({
        id: d.driver_id,
        label: optionLabel(d),
        hay: `${d.driver_name || ''} ${d.driver_id}`.toLowerCase(),
      });
    });
    return list;
  }, [drivers, known, value]);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? options.filter((opt) => (opt.hay || opt.label).toLowerCase().includes(needle) || opt.id.toLowerCase().includes(needle))
    : options;

  const display = (id) => {
    if (!id) return '';
    if (known) return optionLabel(known);
    return `Inactive driver (${id.slice(-6)})`;
  };

  return (
    <div className="relative w-full min-w-0">
      <Combobox
        value={value || ''}
        onChange={(id) => onChange(id ?? '')}
        onClose={() => setQuery('')}
        immediate
      >
        <div className={`flex h-8 items-center rounded-md border bg-white ${error ? 'border-red-400' : 'border-gray-200'}`}>
          <ComboboxInput
            aria-label={ariaLabel}
            displayValue={display}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Select driver…"
            autoComplete="off"
            className="no-theme !block !h-8 min-w-0 flex-1 !border-0 !bg-transparent !py-0 !pl-2 !pr-1 !text-xs !font-medium !text-main-primary !shadow-none placeholder:!text-gray-400 focus:!ring-0"
          />
          <ComboboxButton className="flex h-8 w-7 shrink-0 items-center justify-center text-gray-400">
            <FiChevronDown className="h-3.5 w-3.5" />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          anchor="bottom start"
          className="z-[10000] max-h-56 w-[16rem] overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-2.5 py-1.5 text-xs text-gray-400">No drivers</div>
          ) : filtered.map((opt) => (
            <ComboboxOption
              key={opt.id || 'empty'}
              value={opt.id}
              className="cursor-pointer px-2.5 py-1.5 text-xs text-gray-800 data-[focus]:bg-gray-50 data-[selected]:font-semibold data-[selected]:text-main-primary"
            >
              {opt.label}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
